import { Button } from '@gearbelt/ui/components';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useQuery } from '@tanstack/react-query';

import { AWAAssetDocuments } from '@/awa-pages/[workspaceId]/assets/[id]/details/documents';
import { AWAAssetOverview } from '@/awa-pages/[workspaceId]/assets/[id]/details/overview';
import AttachmentIcon from '@/components/icons/attachment';
import InformationIcon from '@/components/icons/information';
import WarningIcon from '@/components/icons/warning';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { getAssetByIdQueryOptions } from '@/queries/assets';

export function AWAAssetDetails({ assetId }: { assetId: string }) {
  const { data: asset, isLoading: assetLoading, isError: assetFetchError } = useQuery(getAssetByIdQueryOptions({ assetId, isAWA: true }));

  return (
    <div className="flex flex-col overflow-y-auto">
      {assetLoading ? (
        <div className="flex h-full w-full items-center justify-center">
          <div className="border-default-grey-2 size-[30px] animate-spin rounded-full border-4" />
        </div>
      ) : (
        asset && (
          <div className="flex flex-col gap-4">
            <Tabs defaultValue="overview">
              <TabsList className="flex h-[76px] w-full flex-row items-start gap-0 rounded-none bg-white p-5">
                <TabsTrigger value="overview" asChild className="bg-grey-1 data-[state=active]:bg-default-blue rounded-l-[5px] rounded-r-[0px]">
                  <div className="flex h-full w-full flex-row items-center gap-5 p-0">
                    <InformationIcon />
                    <div className="text-base">Overview</div>
                  </div>
                </TabsTrigger>
                <TabsTrigger value="docs" asChild className="bg-grey-1 data-[state=active]:bg-default-blue rounded-l-[0px] rounded-r-[5px]">
                  <div className="flex h-full w-full flex-row items-center gap-5 p-0">
                    <AttachmentIcon />
                    <div className="text-base">Docs</div>
                  </div>
                </TabsTrigger>
              </TabsList>
              <TabsContent className="mt-[0px] p-0 focus-visible:shadow-none focus-visible:ring-0 focus-visible:ring-offset-0" value="overview">
                <AWAAssetOverview asset={asset} />
              </TabsContent>
              <TabsContent className="mt-[0px] p-0 focus-visible:shadow-none focus-visible:ring-0 focus-visible:ring-offset-0" value="docs">
                <AWAAssetDocuments asset={asset} />
              </TabsContent>
            </Tabs>
          </div>
        )
      )}

      <Dialog open={!assetLoading && assetFetchError && !asset}>
        <DialogContent className="w-[354px] rounded-md p-0 sm:w-[500px]">
          <VisuallyHidden>
            <DialogTitle>Error</DialogTitle>
          </VisuallyHidden>
          <DialogHeader>
            <DialogDescription className="flex flex-col items-center gap-2 pt-6">
              <WarningIcon size={30} className="text-default-red" />
              <div className="text-default-text text-xl font-medium">Unable to load</div>
              <div className="text-default-text text-sm font-normal">An error has occurred with loading this asset's details and docs.</div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mb-6 flex items-center sm:flex-col sm:space-x-0">
            <Button
              className="bg-default-blue mt-4 w-fit rounded-md p-2.5 text-sm font-medium"
              onClick={() => {
                location.reload();
              }}>
              Retry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
